import { parse, parseISO, format, isValid } from 'date-fns'
import { fr } from 'date-fns/locale/fr'
import { enGB } from 'date-fns/locale/en-GB'

import { UserLanguages } from '../types'
import i18n from '../i18n'

// TODO Refacto to use understable names and logic
// Add comments to explain the logic

const locales = {
  fr: 'dd/MM/yyyy HH:mm',
  en: 'MM/dd/yyyy HH:mm',
}

const chooseLocale = (locale: UserLanguages) => {
  switch (locale) {
    case 'fr':
      return fr
    case 'en':
      return enGB
    default:
      return fr
  }
}

export const parseLocaleDateTime = <
  T extends string | null | undefined,
  R = T extends string ? Date : undefined,
>(
  date: T
): R => {
  if (date && typeof date === 'string') {
    const parsedDate = parse(date, locales[i18n.resolvedLanguage as UserLanguages], new Date(), {
      locale: chooseLocale(i18n.resolvedLanguage as UserLanguages),
    })
    return isValid(parsedDate) ? (parsedDate as R) : (null as R)
  }
  return undefined as R
}

export const prepareDateTime = (date: string | undefined | null): string =>
  parseLocaleDateTime(date)?.toISOString() ?? ''

export const prepareISODateTime = (date: string | Date | undefined | null): string => {
  const parsedDate = date instanceof Date ? date : parseISO(date ?? '')
  if (isValid(parsedDate)) {
    return format(parsedDate, locales[i18n.resolvedLanguage as UserLanguages], {
      locale: chooseLocale(i18n.resolvedLanguage as UserLanguages),
    })
  }
  return ''
}

export const formatDateTime = (date: string | undefined): string => prepareISODateTime(date)

export const formatHumanDateTime = (date: string | undefined, short?: boolean): string => {
  const parsedDate = parseISO(date ?? '')
  if (isValid(parsedDate)) {
    return format(parsedDate, short ? 'PPp' : 'PPPPp', {
      locale: chooseLocale(i18n.resolvedLanguage as UserLanguages),
    })
  }
  return ''
}

export const formatHumanTime = (date: string | undefined): string => {
  const parsedDate = parseISO(date ?? '')
  if (isValid(parsedDate)) {
    return format(parsedDate, 'p', { locale: chooseLocale(i18n.resolvedLanguage as UserLanguages) })
  }
  return ''
}

export const formatHumanDate = (date: string | undefined, short?: boolean): string => {
  const parsedDate = parseISO(date ?? '')
  if (isValid(parsedDate)) {
    return format(parsedDate, short ? 'PP' : 'PPPP', {
      locale: chooseLocale(i18n.resolvedLanguage as UserLanguages),
    })
  }
  return ''
}

export function compareHours(date1: string | number | Date, date2: string | number | Date): number {
  try {
    const dateTime1 = new Date(date1)
    const dateTime2 = new Date(date2)
    const hour1 = dateTime1.getHours()
    const hour2 = dateTime2.getHours()
    const minute1 = dateTime1.getMinutes()
    const minute2 = dateTime2.getMinutes()

    if (hour1 < hour2) return -1
    if (hour1 > hour2) return 1
    // If hours are equal, compare minutes
    if (minute1 < minute2) return -1
    if (minute1 > minute2) return 1
    // Hours and minutes are equal
    return 0
  } catch (e) {
    console.error('Error while comparing hours and minutes', e)
    return 0
  }
}
